import { Component, OnInit } from '@angular/core';
import { MdDialogRef } from '@angular/material';

export interface TransactionComparison {
  original?: any;
  new?: any;
  action: 'keep' | 'remove' | 'add';
  transactionId?: string;
}

@Component({
  selector: 'app-reanalyze-comparison',
  templateUrl: './reanalyze-comparison.component.html',
  styleUrls: ['./reanalyze-comparison.component.css']
})
export class ReanalyzeComparisonComponent implements OnInit {
  originalTransactions: any[] = [];
  newTransactions: any[] = [];
  comparisons: TransactionComparison[] = [];
  bankStatementId: number;

  constructor(private dialogRef: MdDialogRef<any>) {
    if (this.dialogRef.componentInstance) {
      this.originalTransactions = this.dialogRef.componentInstance.originalTransactions || [];
      this.newTransactions = this.dialogRef.componentInstance.newTransactions || [];
      this.bankStatementId = this.dialogRef.componentInstance.bankStatementId;
      this.buildComparisons();
    }
  }

  ngOnInit() {
  }

  private buildComparisons() {
    // Create a map of original transactions by transaction_id (if they have one)
    const originalMapById = new Map<string, any>();
    const originalTransactionsWithoutId: any[] = [];
    
    this.originalTransactions.forEach(tx => {
      if (tx.transaction_id) {
        originalMapById.set(tx.transaction_id, tx);
      } else {
        originalTransactionsWithoutId.push(tx);
      }
    });

    // Create a key function to match transactions by their data
    const getTransactionKey = (tx: any): string => {
      const date = tx.tx_date || '';
      const amount = tx.tx_amount || '';
      const description = (tx.tx_description || '').substring(0, 50);
      const counterparty = tx.tx_counter_party || '';
      return `${date}::${amount}::${description}::${counterparty}`;
    };

    // Create a map of new transactions by their data key
    const newMapByKey = new Map<string, any>();
    this.newTransactions.forEach(tx => {
      const key = getTransactionKey(tx);
      newMapByKey.set(key, tx);
    });

    // Match transactions: first try by transaction_id, then by data
    const matchedOriginalIds = new Set<string>();
    const matchedDataKeys = new Set<string>();

    // Match by transaction_id first (for transactions that were already pushed)
    originalMapById.forEach((originalTx, txId) => {
      const dataKey = getTransactionKey(originalTx);
      if (newMapByKey.has(dataKey)) {
        matchedOriginalIds.add(txId);
        matchedDataKeys.add(dataKey);
        this.comparisons.push({
          original: originalTx,
          new: newMapByKey.get(dataKey),
          action: 'keep',
          transactionId: txId
        });
      }
    });

    // Match remaining original transactions by data
    originalTransactionsWithoutId.forEach(originalTx => {
      const dataKey = getTransactionKey(originalTx);
      if (newMapByKey.has(dataKey) && !matchedDataKeys.has(dataKey)) {
        matchedDataKeys.add(dataKey);
        this.comparisons.push({
          original: originalTx,
          new: newMapByKey.get(dataKey),
          action: 'keep'
        });
      }
    });

    // Add original transactions that don't exist in new (to be removed)
    originalMapById.forEach((originalTx, txId) => {
      if (!matchedOriginalIds.has(txId)) {
        this.comparisons.push({
          original: originalTx,
          action: 'remove',
          transactionId: txId
        });
      }
    });
    
    originalTransactionsWithoutId.forEach(originalTx => {
      const dataKey = getTransactionKey(originalTx);
      if (!matchedDataKeys.has(dataKey)) {
        this.comparisons.push({
          original: originalTx,
          action: 'remove'
        });
      }
    });

    // Add new transactions that don't exist in original (to be added)
    newMapByKey.forEach((newTx, dataKey) => {
      if (!matchedDataKeys.has(dataKey)) {
        this.comparisons.push({
          new: newTx,
          action: 'add'
        });
      }
    });
  }

  selectAllOriginal() {
    this.comparisons.forEach(comp => {
      if (comp.original && !comp.new) {
        comp.action = 'remove';
      } else if (comp.original && comp.new) {
        comp.action = 'keep';
      } else if (comp.new && !comp.original) {
        comp.action = 'add';
      }
    });
  }

  selectAllNew() {
    this.comparisons.forEach(comp => {
      if (comp.original && !comp.new) {
        comp.action = 'remove';
      } else if (comp.original && comp.new) {
        comp.action = 'keep';
      } else if (comp.new && !comp.original) {
        comp.action = 'add';
      }
    });
  }

  toggleTransaction(comparison: TransactionComparison) {
    if (comparison.original && comparison.new) {
      // Both exist - toggle between keep and remove
      comparison.action = comparison.action === 'keep' ? 'remove' : 'keep';
    } else if (comparison.original && !comparison.new) {
      // Only original - toggle remove
      comparison.action = comparison.action === 'remove' ? 'keep' : 'remove';
    } else if (comparison.new && !comparison.original) {
      // Only new - toggle add
      comparison.action = comparison.action === 'add' ? 'remove' : 'add';
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    const result = {
      toRemove: this.comparisons
        .filter(c => c.action === 'remove' && c.transactionId)
        .map(c => c.transactionId),
      toAdd: this.comparisons
        .filter(c => c.action === 'add' && c.new)
        .map(c => c.new),
      toKeep: this.comparisons
        .filter(c => c.action === 'keep' && c.original)
        .map(c => c.original)
    };
    this.dialogRef.close(result);
  }

  getActionLabel(comparison: TransactionComparison): string {
    if (comparison.original && comparison.new) {
      return comparison.action === 'keep' ? 'Keep' : 'Remove';
    } else if (comparison.original && !comparison.new) {
      return comparison.action === 'remove' ? 'Remove' : 'Keep';
    } else if (comparison.new && !comparison.original) {
      return comparison.action === 'add' ? 'Add' : 'Skip';
    }
    return '';
  }
}




// WEBPACK FOOTER //
// ./src/app/entities/finance/reanalyze-comparison/reanalyze-comparison.component.ts